<template>
    <v-card
        class="product-list-item pb-4 d-flex flex-column flex-grow-1 h-100"
        elevation="1"
        itemtype="https://schema.org/Product"
        itemscope
    >
        <div
            class="d-none"
            itemtype="https://schema.org/Brand"
            itemprop="brand"
            itemscope
        >
            <meta itemprop="name" :content="brandTitle" />
        </div>

        <ProductFlags
            v-if="item.flags"
            class="card-top-product-flags"
            v-model="item.flags"
            density="compact"
            size="large"
            label
        ></ProductFlags>

        <NuxtLink
            ref="elImgLink"
            :to="itemUrl"
            class="product-list-item-image-link d-block position-relative overflow-hidden"
            :class="{ 'pa-4': isCutoutImage }"
            :style="imgMinHeight ? { minHeight: `${imgMinHeight}px` } : undefined"
            @touchstart.passive="onImageTouchStart"
            @touchmove.passive="onImageTouchMove"
            @touchend.passive="onImageTouchEnd"
            @touchcancel.passive="onImageTouchEnd"
        >
            <FullImage
                class="w-100 d-table"
                :key="`prod-img-id-${selectedSku?.defaultImage?.uid || useId()}`"
                :image-object="selectedSku?.defaultImage"
                :type="['small', 'medium']"
                :style="currentPaneStyle"
            ></FullImage>

            <!-- Adjacent colors slide in while swiping (touch) -->
            <div
                v-if="dragActive && prevColorImage"
                class="product-list-item-swipe-pane"
                :class="{ 'pa-4': !!prevColorImage.showDropShadow }"
                :style="prevPaneStyle"
            >
                <FullImage
                    class="w-100 h-100 object-fit-contain"
                    :key="`prod-img-prev-${prevColorImage.uid}`"
                    :image-object="prevColorImage"
                    :type="['small', 'medium']"
                ></FullImage>
            </div>
            <div
                v-if="dragActive && nextColorImage"
                class="product-list-item-swipe-pane"
                :class="{ 'pa-4': !!nextColorImage.showDropShadow }"
                :style="nextPaneStyle"
            >
                <FullImage
                    class="w-100 h-100 object-fit-contain"
                    :key="`prod-img-next-${nextColorImage.uid}`"
                    :image-object="nextColorImage"
                    :type="['small', 'medium']"
                ></FullImage>
            </div>

            <!-- Secondary shot cross-faded in on card hover (desktop only) -->
            <div
                v-if="hoverImage"
                class="product-list-item-hover-image position-absolute"
                :class="{ 'pa-4': isHoverCutoutImage }"
            >
                <FullImage
                    class="w-100 h-100 object-fit-contain"
                    :key="`prod-img-hover-${hoverImage.uid}`"
                    :image-object="hoverImage"
                    :type="['small', 'medium']"
                ></FullImage>
            </div>
        </NuxtLink>

        <div
            class="position-relative d-flex flex-column flex-grow-1"
        >

            <v-divider
                class="mb-4"
            ></v-divider>

            <DebugContent>
                <details
                    class="mb-2"
                >
                    <summary
                        class="cursor-pointer"
                    >
                        Options Debug Info
                    </summary>
                    <div>
                        <h5
                            class="py-0 my-0"
                        >
                            Model
                        </h5>
                        <ul>
                            <li
                                v-for="(v, k) in optionsModel"
                                :key="`optionsModel-opt-${k}`"
                            >
                                <strong>{{ k }}:</strong>
                                {{ v }}
                            </li>
                        </ul>

                        <h5
                            class="py-0 my-0"
                        >
                            Options List
                        </h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsList"
                                :key="`optionsList-opt-${i}`"
                                :class="{
                                    'text-success': opt.showOnCollectionPages
                                }"
                            >
                                {{ opt.attribute.uid }}
                            </li>
                        </ul>

                        <h5
                            class="py-0 my-0"
                        >
                            Disabled Model
                        </h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsDisabledModel"
                                :key="`optionsDisabledModel-opt-${i}`"
                            >
                                {{ opt }}
                            </li>
                        </ul>
                    </div>
                </details>
            </DebugContent>

            <!-- Options -->
            <ProductsListOptions
                class="px-4"
                :options-model="optionsModel"
                :options-list="optionsList"
                :options-disabled-model="optionsDisabledModel"
                @on-change="onOptionChange"
                hide-label
            ></ProductsListOptions>

            <div
                v-if="selectedColorNames"
                class="px-4 font-size-9 text-medium-emphasis"
            >
                {{ selectedColorNames }}
            </div>

            <NuxtLink
                :to="itemUrl"
                class="text-decoration-none d-flex flex-column mt-auto px-4"
            >
                <!-- Item Title -->

                <div
                    class="product-list-item-title mt-4 mb-0"
                >
                    <ProductTitle
                        :product="item"
                        :compact="true"
                        :font-size-px="20"
                        natural-case
                        no-bold
                    ></ProductTitle>
                    <ProductRatingTitle
                        class="mt-1"
                        :average="item.averageRating"
                        :total="item.reviewCount"
                    ></ProductRatingTitle>

                    <meta itemprop="description" :content="item.description" />
                </div>

                <!-- Price -->
                <div
                    v-if="selectedSku"
                    class="mt-1 d-flex flex-column"
                >
                    <span
                        v-if="currentMinPrice"
                        class="v-align-middle"
                    >
                        <span class="text-medium-emphasis">from</span> <span class="font-weight-medium">{{ formatCurrency(currentMinPrice) }}</span>
                    </span>
                    <ProductPriceInfo
                        v-else
                        class="d-flex flex-column"
                        :current-sku="selectedSku"
                        size="sm"
                        order-reverse
                        no-bold
                    ></ProductPriceInfo>
                </div>
            </NuxtLink>

        </div>
    </v-card>
</template>

<script lang="ts" setup>
    import type {
        Product,
        Sku,
        FullImage as FullImageType,
        SkusMap,
        ProductAttrsMaps,
        ProductAttrsGroupOption,
        ProductOptionsModel,
        ProductAttrsDisabledModel
    } from '~/types';

    import {
        useSkusUtils,
        useProductAttrs,
        useIsMobile,
    } from '~/composables';
    import {
        useCustomNuxtUtils
    } from 'x1-base/composables';

    import { pathJoin, formatCurrency } from 'x1-base/utils';
    import ProductTitle from '~/components/products/product/ProductTitle/ProductTitle.vue';
    import ProductRatingTitle from '~/components/products/ProductRatingTitle/ProductRatingTitle.vue';
    import ProductFlags from '~/components/products/ProductFlags/ProductFlags.vue';
    import FullImage from '~/components/media/FullImage/FullImage.vue';
    import ProductPriceInfo from '~/components/products/ProductPriceInfo/ProductPriceInfo.vue';

    import ProductsListOptions from '~/components/products/products-list/ProductsListOptions/ProductsListOptions.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import { isUndefined as lo_isUndefined } from 'es-toolkit';
    import {
        ProductListItemStateKeyPrefix
    } from '~/constants';

    const props = withDefaults(
        defineProps<{
            item: Product;
            selectFirst: boolean;
            brandTitle: string;
            pageKey: string;
        }>(),
        {
            selectFirst: false
        }
    );

    const {
        getSkusMap,
        getSkuByUid,
        getValuesModelBySku,
        findFirstSkuByPartialOptions
    } = useSkusUtils();

    const {
        processAttrsGroups
    } = useProductAttrs();

    const {
        removeOnceKey
    } = useCustomNuxtUtils();

    const debugConsole = useDebug();

    const {
        skus,
        optionGroups,
        defaultSkuUid,
        uid: itemUid
    } = props.item;


    const selectedSku = useState<Nilish<Sku>>(`${ProductListItemStateKeyPrefix}SelectedSku_${itemUid}`, () => null);
    const itemUrl = computed<string>(() => pathJoin('/', selectedSku.value?.path || ''));
    // Transparent cutout images (the only ones that get a drop shadow) read
    // better inset from the card edges; opaque photos run full-bleed.
    const isCutoutImage = computed<boolean>(() => !!selectedSku.value?.defaultImage?.showDropShadow);

    // Hover (secondary) image: rendered hidden and cross-faded in on card
    // hover, following the selected sku so it always matches the shown
    // color. Client-mounted + fine-pointer only, so touch devices never
    // download it.
    const { isTouch } = useIsMobile();
    const isMounted = ref(false);

    const hoverImage = computed<Nullable<FullImageType>>(() => {
        if (!isMounted.value || isTouch.value) {
            return null;
        }
        return selectedSku.value?.secondaryImage ?? null;
    });
    const isHoverCutoutImage = computed<boolean>(() => !!hoverImage.value?.showDropShadow);

    // Swiping the image horizontally cycles through the colors (touch only),
    // stepping the first color option group the same way tapping its
    // swatches would.
    function cycleColor(step: number): void {
        const colorOpt = optionsList.value.find((opt) => opt.showOnCollectionPages && opt.attribute.isColor);
        if (!colorOpt || colorOpt.groupValues.length < 2) {
            return;
        }

        const uidAttr = colorOpt.attribute.uid;
        const values = colorOpt.groupValues;
        const currentIndex = values.findIndex((gv) => gv.value.uid === optionsModel.value[uidAttr]);
        const next = values[(currentIndex + step + values.length) % values.length];

        if (!next) {
            return;
        }

        optionsModel.value = Object.assign({}, optionsModel.value, { [uidAttr]: next.value.uid });
        onOptionChange(optionsModel.value);
    }

    // Drag state: the image tracks the finger and the adjacent color's image
    // slides in beside it; releasing past the threshold settles the slide and
    // commits the color switch, otherwise it springs back.
    const dragX = ref(0);
    const dragActive = ref(false);
    const dragSettling = ref(false);
    let touchStartX = 0;
    let touchStartY = 0;
    let touchTracking = false;
    let horizontalIntent = false;

    function whitelistedOptionsModel(): Record<string, string> {
        return attrsWhitelist.value.reduce((acc: Record<string, string>, uid: string) => {
            if (optionsModel.value[uid]) acc[uid] = optionsModel.value[uid];
            return acc;
        }, {});
    }

    function adjacentColorSku(step: number): Nullable<Sku> {
        const colorOpt = optionsList.value.find((opt) => opt.showOnCollectionPages && opt.attribute.isColor);
        if (!colorOpt || colorOpt.groupValues.length < 2) {
            return null;
        }

        const uidAttr = colorOpt.attribute.uid;
        const values = colorOpt.groupValues;
        const currentIndex = values.findIndex((gv) => gv.value.uid === optionsModel.value[uidAttr]);
        const adjacent = values[(currentIndex + step + values.length) % values.length];

        if (!adjacent) {
            return null;
        }

        const model = Object.assign(whitelistedOptionsModel(), { [uidAttr]: adjacent.value.uid });
        return findFirstSkuByPartialOptions(skus, model) ?? null;
    }

    const hasMultipleColors = computed<boolean>(() => {
        const colorOpt = optionsList.value.find((opt) => opt.showOnCollectionPages && opt.attribute.isColor);
        return !!colorOpt && colorOpt.groupValues.length > 1;
    });

    const prevColorImage = computed<Nullable<FullImageType>>(() => dragActive.value ? adjacentColorSku(-1)?.defaultImage ?? null : null);
    const nextColorImage = computed<Nullable<FullImageType>>(() => dragActive.value ? adjacentColorSku(1)?.defaultImage ?? null : null);

    const dragTransition = computed<string>(() => dragSettling.value ? 'transform 0.2s ease' : 'none');
    const currentPaneStyle = computed(() => dragActive.value
        ? { transform: `translateX(${dragX.value}px)`, transition: dragTransition.value }
        : undefined);
    const prevPaneStyle = computed(() => ({
        transform: `translateX(calc(-100% + ${dragX.value}px))`,
        transition: dragTransition.value
    }));
    const nextPaneStyle = computed(() => ({
        transform: `translateX(calc(100% + ${dragX.value}px))`,
        transition: dragTransition.value
    }));

    function resetDrag(): void {
        touchTracking = false;
        horizontalIntent = false;
        dragSettling.value = false;
        dragX.value = 0;
        dragActive.value = false;
    }

    function onImageTouchStart(e: TouchEvent): void {
        if (e.touches.length !== 1 || !hasMultipleColors.value || dragSettling.value) {
            return;
        }
        touchTracking = true;
        horizontalIntent = false;
        touchStartX = e.touches[0]!.clientX;
        touchStartY = e.touches[0]!.clientY;
        dragX.value = 0;
        // Render (and start loading) the neighbor images on finger-down so
        // they're ready by the time the drag reveals them.
        dragActive.value = true;
    }

    function onImageTouchMove(e: TouchEvent): void {
        if (!touchTracking || e.touches.length !== 1) {
            return;
        }

        const dx = e.touches[0]!.clientX - touchStartX;
        const dy = e.touches[0]!.clientY - touchStartY;

        // Track only predominantly-horizontal drags so vertical page
        // scrolling is never hijacked (listeners are passive).
        if (!horizontalIntent) {
            if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
                horizontalIntent = true;
            } else if (Math.abs(dy) > 12) {
                resetDrag();
                return;
            } else {
                return;
            }
        }

        dragX.value = dx;
    }

    function onImageTouchEnd(): void {
        if (!touchTracking) {
            return;
        }
        touchTracking = false;

        if (!horizontalIntent) {
            resetDrag();
            return;
        }

        const width = elImgLink.value?.$el?.offsetWidth || 300;
        const dx = dragX.value;
        const commit = Math.abs(dx) > Math.min(80, width * 0.25);
        dragSettling.value = true;

        if (commit) {
            const step = dx < 0 ? 1 : -1;
            dragX.value = dx < 0 ? -width : width;
            setTimeout(() => {
                cycleColor(step);
                resetDrag();
            }, 200);
        } else {
            dragX.value = 0;
            setTimeout(() => {
                resetDrag();
            }, 200);
        }
    }

    // Names of the currently selected color(s), e.g. "Navy" or "Black / White"
    // for multi-color products.
    const selectedColorNames = computed<string>(() => {
        return optionsList.value
            .filter((opt) => opt.showOnCollectionPages && opt.attribute.isColor)
            .map((opt) => opt.groupValues.find((gv) => gv.value.uid === optionsModel.value[opt.attribute.uid])?.value.name)
            .filter(Boolean)
            .join(' / ');
    });

    // Switching skus remounts FullImage (keyed on image uid), so the image
    // area collapses to 0px until the new image loads and the info block
    // flashes at the top of the tile. Hold the previous height until then.
    const elImgLink = shallowRef<Nullable<{ $el: HTMLElement }>>(null);
    const imgMinHeight = ref<Nullable<number>>(null);
    let imgSwapToken = 0;

    watch(
        () => selectedSku.value?.uid,
        async (uid, oldUid) => {
            if (!uid || !oldUid || uid === oldUid) {
                return;
            }

            const el = elImgLink.value?.$el;
            if (!el) {
                return;
            }

            const token = ++imgSwapToken;
            imgMinHeight.value = el.offsetHeight;

            await nextTick();

            const img = el.querySelector('img');
            if (img && !img.complete) {
                await new Promise<void>((resolve) => {
                    img.addEventListener('load', () => resolve(), { once: true });
                    img.addEventListener('error', () => resolve(), { once: true });
                });
            }

            if (token === imgSwapToken) {
                imgMinHeight.value = null;
            }
        }
    );
    const skusMap = useState<Nilish<SkusMap>>(`${ProductListItemStateKeyPrefix}SkusMap_${itemUid}`, () => null);
    const currentMinPrice = computed<Nullable<number>>(() => !lo_isUndefined(props.item?.minPrice) ? props.item.minPrice : null);

    const attrsMapOptions = useState<Nilish<ProductAttrsMaps>>(`${ProductListItemStateKeyPrefix}AttrsMapOpts_${itemUid}`, () => null);
    const optionsDisabledModel = ref<ProductAttrsDisabledModel>({});
    // const optionsInvalidValuesCombos = useState<string[][]>('keyProdListFormOptInvValCombos', () => []);

    const optionsList = ref<ProductAttrsGroupOption[]>(optionGroups);
    const optionsModel = useState<ProductOptionsModel>(`${ProductListItemStateKeyPrefix}OptsModel_${itemUid}`, () => ({}));
    const attrsWhitelist = ref<string[]>([]);

    const onceKey = `${props.pageKey}_${itemUid}`;

    callOnce(onceKey, () => {
        skusMap.value = getSkusMap(skus);
        attrsMapOptions.value = processAttrsGroups(optionGroups);
        attrsWhitelist.value = optionGroups.reduce((acc: string[], opt) => {
            if (opt.showOnCollectionPages) acc.push(opt.attribute.uid);
            return acc;
        }, []);


        selectedSku.value = getSkuByUid(skus, defaultSkuUid) || skus[0];
        const skuUid = selectedSku.value?.uid;

        if (skuUid) {
            optionsModel.value = getValuesModelBySku(skusMap.value, attrsMapOptions.value.attrs, skuUid);
        }
    }, { mode: 'navigation' });

    function onOptionChange(updatedModel: ProductOptionsModel): void {
        const model = attrsWhitelist.value.reduce((acc: Record<string, string>, uid: string) => {
            if (updatedModel[uid]) acc[uid] = updatedModel[uid];
            return acc;
        }, {});

        const sku = findFirstSkuByPartialOptions(skus, model);

        if (sku) {
            selectedSku.value = sku;
            return;
        }

        debugConsole.warn('Select Sku by partial props not found... use default (or first) sku');
        selectedSku.value = getSkuByUid(skus, props.item.defaultSkuUid) || skus[0];
    }

    onMounted(() => {
        isMounted.value = true;
        removeOnceKey(onceKey);
    });

    onBeforeUnmount(() => {
        removeOnceKey(onceKey);
    });
</script>

<style lang="scss">
.product-list-item {
    // ProductTitle centers itself below the md breakpoint; collection cards
    // are left-aligned at every size.
    .product-title.text-center {
        text-align: start !important;
    }

    .product-title .justify-center {
        justify-content: flex-start !important;
    }

    // The theme maps font-weight-regular to 500 (vuetify.scss) and the
    // font-size-6 utility pins 20px, so taglines render exactly like the
    // natural-case title. Force true regular at 16px here.
    .product-title h4.font-weight-regular {
        font-weight: 400 !important;
        font-size: 16px !important;
        margin-top: 6px !important;
    }

    .product-list-item-image-link {
        // Once the browser classifies a gesture on the image as horizontal,
        // it must not also vertical-scroll the page mid-swipe; pan-y leaves
        // vertical scrolling native and hands horizontal gestures to us
        // exclusively. A gesture the browser claims as a vertical scroll
        // reaches us as touchcancel, which resets the drag.
        touch-action: pan-y;
    }

    .product-list-item-swipe-pane {
        position: absolute;
        inset: 0;
        background-color: rgb(var(--v-theme-surface));
    }

    .product-list-item-hover-image {
        inset: 0;
        opacity: 0;
        transition: opacity 0.2s ease;
        // Fully cover the default image during the fade, including behind
        // transparent cutouts and letterboxing.
        background-color: rgb(var(--v-theme-surface));
    }

    .product-list-item-image-link:hover .product-list-item-hover-image {
        opacity: 1;
    }
}
</style>
